'use client';

import { useEnv } from '@/env/client';
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { useId } from 'react';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faBasketShopping, faWrench, faScrewdriverWrench, faHandshake } from '@fortawesome/free-solid-svg-icons';
import { LocationCustomPin } from '@/components/LocationCustomPin';

export interface LocationImageAndInfoMapLocation {
  title: string;
  lat: number;
  lng: number;
  placeId?: string;
}

const getIconForLocationType = (title: string): IconDefinition => {
  const lowerTitle = title.toLowerCase();
  if (lowerTitle.includes('service')) return faWrench;
  if (lowerTitle.includes('parts')) return faScrewdriverWrench;
  if (lowerTitle.includes('trade')) return faHandshake;
  return faBasketShopping; // Default for Sales or unknown
};

const getLocationsCenter = (locations: LocationImageAndInfoMapLocation[]) => {
  if (locations.length === 0) {
    return { lat: 0, lng: 0 };
  }

  const latitudes = locations.map((loc) => loc.lat);
  const longitudes = locations.map((loc) => loc.lng);

  const lat = (Math.min(...latitudes) + Math.max(...latitudes)) / 2;
  const lng = (Math.min(...longitudes) + Math.max(...longitudes)) / 2;

  return { lat, lng };
};

const getDefaultZoom = (locations: LocationImageAndInfoMapLocation[]) => {
  if (locations.length === 0) {
    return 1;
  }

  const latitudes = locations.map((loc) => loc.lat);
  const longitudes = locations.map((loc) => loc.lng);

  const latDiff = Math.max(...latitudes) - Math.min(...latitudes);
  const lngDiff = Math.max(...longitudes) - Math.min(...longitudes);

  const maxDiff = Math.max(latDiff, lngDiff);

  if (maxDiff < 0.01) return 16;
  if (maxDiff < 0.1) return 13;
  if (maxDiff < 1) return 11;
  if (maxDiff < 5) return 9;
  if (maxDiff < 10) return 7;
  return 5;
};

interface LocationImageAndInfoMapProps {
  locations: LocationImageAndInfoMapLocation[];
}

export const LocationImageAndInfoMap = ({ locations }: LocationImageAndInfoMapProps) => {
  const { env } = useEnv();
  const id = useId();

  return (
    <APIProvider apiKey={env.GOOGLE_MAPS_API_KEY}>
      <Map mapId={id} defaultCenter={getLocationsCenter(locations)} defaultZoom={getDefaultZoom(locations)}>
        {locations.map((loc) => (
          <AdvancedMarker key={loc.title} position={{ lat: loc.lat, lng: loc.lng }} title={loc.title}>
            <LocationCustomPin
              title={locations.length > 1 ? loc.title : undefined}
              icon={getIconForLocationType(loc.title)}
            />
          </AdvancedMarker>
        ))}
      </Map>
    </APIProvider>
  );
};
